import React, { createContext, useReducer, useContext, ReactNode } from "react";
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { AuthContext } from "./AuthContext";

interface AppState {
  isLoading: boolean;
}

interface StartLoadingAction {
  type: "START_LOADING";
}

interface StopLoadingAction {
  type: "STOP_LOADING";
}

type AppAction = StartLoadingAction | StopLoadingAction;

interface AppContextProps {
  state: AppState;
  fetchData: <T = any>(
    url: string,
    method?: AxiosRequestConfig["method"],
    data?: AxiosRequestConfig["data"],
    headers?: AxiosHeaders,
    config?: Partial<AxiosRequestConfig>
  ) => Promise<AxiosResponse<T>>;
}
const FetchContext = createContext<AppContextProps | undefined>(undefined);

const actionTypes = {
  START_LOADING: "START_LOADING" as const,
  STOP_LOADING: "STOP_LOADING" as const,
};

const reducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case actionTypes.START_LOADING:
      return { ...state, isLoading: true };
    case actionTypes.STOP_LOADING:
      return { ...state, isLoading: false };
    default:
      return state;
  }
};

interface AppProviderProps {
  children: ReactNode;
}

export const FetchProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, { isLoading: false });
  const { logout, userData } = useContext(AuthContext);

  const startLoading = () => {
    dispatch({ type: actionTypes.START_LOADING });
  };

  const stopLoading = () => {
    dispatch({ type: actionTypes.STOP_LOADING });
  };

  // Nel FetchProvider, modifica la funzione fetchData per accettare un oggetto config completo:
  const fetchData = async <T,>(
    url: string,
    method: AxiosRequestConfig["method"] = "GET",
    data?: AxiosRequestConfig["data"],
    headers?: AxiosHeaders,
    config?: Partial<AxiosRequestConfig>
  ): Promise<AxiosResponse<T> | any> => {
    try {
      startLoading();

      const axiosConfig: AxiosRequestConfig = {
        method,
        url: process.env.REACT_APP_API_URL + url,
        headers: {
          Authorization: `Bearer ${userData?.accessToken}`,
          ...(headers || ({} as any)),
        },
        data,
        ...config, // Merge di eventuali configurazioni aggiuntive
      };

      const response: AxiosResponse = await axios(axiosConfig);
      return response;
    } catch (error) {
      console.error("Errore nella chiamata API:", error);
      if (axios.isAxiosError(error)) {
        if (error.response?.data.statusCode === 401) {
          logout();
        }
        const axiosError: AxiosError = error;
        throw axiosError.response?.data ?? axiosError;
      } else {
        throw new Error(`Unexpected error: ${error}`);
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <FetchContext.Provider value={{ state, fetchData }}>
      {children}
    </FetchContext.Provider>
  );
};

export const useFetchContext = (): AppContextProps => {
  const context = useContext(FetchContext);
  if (!context) {
    throw new Error("useFetchContext must be used within FetchProvider");
  }
  return context;
};
